import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector, useAppStore } from '@repo/store/maps/hooks';
import { selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import { selectIsMiniMapMode } from '@repo/store/maps/slices/clientContext';
import { selectFogCircles, selectFogHydrationVersion, selectFogOfWarEnabled, selectLocationEnabled, selectMapConfig, selectMapMetadata, selectTitleName } from '@repo/store/maps/slices/mapData';
import { HostType } from '@repo/types/host';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast as hotToast } from 'react-hot-toast';
import { LAYER_CONFIG } from '../game-map/hooks/object3d-constants';
import { useMapContext } from '../game-map/map-context';
import { useMapDownloadPath } from '../utils/use-map-download-path';
import { FOG_LAYER_ID, FogOfWarLayer } from './fog-of-war-layer';
const FOG_READY_TIMEOUT_MS = 4000;

/** Insert fog above `map-tiles` / background but below markers, 3D, trails — same rule as `useObject3dRenderer`. */
function getFogInsertBeforeLayerId(map: MapLibre.Map): string | undefined {
  const layers = map.getStyle()?.layers;
  if (!layers) return undefined;
  for (const layer of layers) {
    if (layer.id === 'map-tiles') continue;
    if (layer.type === 'background') continue;
    if (layer.id === FOG_LAYER_ID) continue;
    if (layer.id.startsWith(LAYER_CONFIG.LAYER_ID_PREFIX)) continue;
    return layer.id;
  }
  return undefined;
}
function syncFogLayerOrder(map: MapLibre.Map): void {
  try {
    if (!map.getStyle()?.layers) return;
    if (!map.getLayer(FOG_LAYER_ID)) return;
    const beforeId = getFogInsertBeforeLayerId(map);
    if (!beforeId || !map.getLayer(beforeId)) return;
    map.moveLayer(FOG_LAYER_ID, beforeId);
  } catch {
    // Style not ready or mid-update.
  }
}
function parseColorToRgb(input: string | undefined | null): [number, number, number] | null {
  if (!input) return null;
  const hex = input.trim();
  if (hex.startsWith('#')) {
    const h = hex.slice(1);
    if (h.length === 3) {
      const r = parseInt((h[0] ?? '') + (h[0] ?? ''), 16);
      const g = parseInt((h[1] ?? '') + (h[1] ?? ''), 16);
      const b = parseInt((h[2] ?? '') + (h[2] ?? ''), 16);
      if ([r, g, b].every(Number.isFinite)) return [r / 255, g / 255, b / 255];
    } else if (h.length === 6) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if ([r, g, b].every(Number.isFinite)) return [r / 255, g / 255, b / 255];
    }
  }
  const rgbMatch = hex.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return [Number(rgbMatch[1] ?? 0) / 255, Number(rgbMatch[2] ?? 0) / 255, Number(rgbMatch[3] ?? 0) / 255];
  }
  return null;
}
export function useFogOfWarLayer() {
  const {
    map
  } = useMapContext();
  const store = useAppStore();
  const enabled = useAppSelector(selectFogOfWarEnabled);
  const metadata = useAppSelector(selectMapMetadata);
  const mapConfig = useAppSelector(selectMapConfig);
  const downloadPath = useMapDownloadPath('fog_of_war_toast', {
    mapId: mapConfig?.externalId,
    titleId: mapConfig?.titleId
  }, `${process.env.NEXT_PUBLIC_WAND_WEBSITE_URL}/download`);
  const {
    t
  } = useT(['game_map']);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const downloadPathRef = useRef(downloadPath);
  downloadPathRef.current = downloadPath;
  const isMiniMapMode = useAppSelector(selectIsMiniMapMode);
  const isMiniMapModeRef = useRef(isMiniMapMode);
  const mapBgColor = useMemo(() => parseColorToRgb(metadata?.backgroundColor), [metadata?.backgroundColor]);
  const mapBgColorRef = useRef(mapBgColor);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const hydrationVersion = useAppSelector(selectFogHydrationVersion);
  const fogLayerRef = useRef<FogOfWarLayer | null>(null);
  const [isFogLoginModalOpen, setIsFogLoginModalOpen] = useState(false);
  const [fogReady, setFogReady] = useState(false);
  const initialFogLoadCompleteRef = useRef(false);

  // Gate loading-screen drop on MapLibre `idle` so first paint is visually complete.
  // `prefsReady` covers the window before this; once set, fogReady stays true for the session.
  useEffect(() => {
    if (initialFogLoadCompleteRef.current) return;
    if (!map) return;
    const markReady = () => {
      if (initialFogLoadCompleteRef.current) return;
      initialFogLoadCompleteRef.current = true;
      setFogReady(true);
    };
    if (map.loaded()) {
      markReady();
      return;
    }
    map.on('idle', markReady);
    const timeoutId = setTimeout(markReady, FOG_READY_TIMEOUT_MS);
    return () => {
      map.off('idle', markReady);
      clearTimeout(timeoutId);
    };
  }, [map]);
  useEffect(() => {
    mapBgColorRef.current = mapBgColor;
    if (map && enabled) map.triggerRepaint();
  }, [mapBgColor, map, enabled]);

  // Subscribe rather than useAppSelector(selectFogCircles): the selector returns a fresh array ref
  // on every reveal, which would re-render this component on every player tick.
  useEffect(() => {
    if (!map) return;
    let prevLen = selectFogCircles(store.getState()).length;
    const unsub = store.subscribe(() => {
      if (!enabledRef.current) return;
      const curLen = selectFogCircles(store.getState()).length;
      if (curLen !== prevLen) {
        prevLen = curLen;
        map.triggerRepaint();
      }
    });
    return unsub;
  }, [map, store]);
  useEffect(() => {
    if (map && enabled) map.triggerRepaint();
  }, [enabled, map]);
  const persistentToastIdRef = useRef<string | null>(null);
  const dismissedToastTypeRef = useRef<'login' | 'open-game' | null>(null);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const titleName = useAppSelector(selectTitleName);
  const dismissPersistentToast = useCallback(() => {
    if (persistentToastIdRef.current) {
      hotToast.remove(persistentToastIdRef.current);
      persistentToastIdRef.current = null;
    }
  }, []);
  useEffect(() => {
    if (!enabled) {
      dismissPersistentToast();
      dismissedToastTypeRef.current = null;
      return;
    }
    let toastType: 'login' | 'open-game' | null = null;
    if (!isAuthenticated) {
      toastType = 'login';
    } else if (!locationEnabled && hostType === HostType.WEB) {
      toastType = 'open-game';
    }
    if (!toastType) {
      dismissPersistentToast();
      dismissedToastTypeRef.current = null;
      return;
    }
    if (dismissedToastTypeRef.current === toastType) {
      return;
    }
    dismissedToastTypeRef.current = null;
    if (persistentToastIdRef.current) {
      dismissPersistentToast();
    }
    const handleDownloadClick = () => {
      if (analytics && mapConfig?.externalId && hostType && titleName) {
        analytics.event('game_map_fog_of_war_download_wand_click', {
          mapId: mapConfig.externalId,
          titleId: mapConfig.titleId,
          titleName,
          platform: hostType
        });
      }
      window.open(downloadPathRef.current, '_blank');
    };
    const currentType = toastType;
    const id = hotToast.custom(toastItem => <div className={css({
      backgroundColor: 'grey.900',
      borderRadius: '8',
      boxShadow: 'lg',
      p: '12',
      pr: '36',
      mt: '48',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: '8',
      minWidth: 'xs',
      maxWidth: 'sm',
      pointerEvents: 'auto'
    })}>
                    <span className={css({
        color: 'text.primary',
        fontSize: 'md',
        flex: '1'
      })}>
                        {currentType === 'login' ? t($ => $.fog_of_war_login_toast) : t($_0 => $_0.fog_of_war_open_game_toast)}{' '}
                        <button type="button" className={css({
          color: 'brand.primary',
          fontSize: 'md',
          fontWeight: 'medium',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          background: 'transparent',
          border: 'none',
          padding: '0',
          _hover: {
            textDecoration: 'underline'
          }
        })} onClick={currentType === 'login' ? () => setIsFogLoginModalOpen(true) : handleDownloadClick}>
                            {currentType === 'login' ? t($_1 => $_1.fog_of_war_login_action) : t($_2 => $_2.fog_of_war_download_wand_action)}
                        </button>
                    </span>
                    <IconButton variant="ghost" onClick={() => {
        dismissedToastTypeRef.current = currentType;
        hotToast.remove(toastItem.id);
        if (persistentToastIdRef.current === toastItem.id) {
          persistentToastIdRef.current = null;
        }
      }} color="primary" size="sm" aria-label="Close" className={css({
        position: 'absolute',
        top: '[50%]',
        right: '8',
        transform: 'translate(0, -50%)'
      })}>
                        <Icon icon="close" decorative />
                    </IconButton>
                </div>, {
      duration: Number.POSITIVE_INFINITY,
      position: 'top-center'
    });
    persistentToastIdRef.current = id;
  }, [enabled, isAuthenticated, locationEnabled, t, analytics, hostType, mapConfig, titleName, dismissPersistentToast]);
  useEffect(() => {
    return () => dismissPersistentToast();
  }, [dismissPersistentToast]);
  useEffect(() => {
    if (!map || !enabled) return;
    const prevMaxPitch = map.getMaxPitch();
    const dragRotateWasEnabled = map.dragRotate.isEnabled();
    const touchRotateWasEnabled = map.touchZoomRotate.isEnabled();
    map.easeTo({
      pitch: 0,
      bearing: 0,
      duration: 300
    });
    map.setMaxPitch(0);
    if (dragRotateWasEnabled) map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    return () => {
      try {
        map.setMaxPitch(prevMaxPitch);
        if (dragRotateWasEnabled) map.dragRotate.enable();
        if (touchRotateWasEnabled) map.touchZoomRotate.enableRotation();
        map.keyboard.enableRotation();
      } catch {}
    };
  }, [map, enabled]);

  // Add the layer once and never remove on disable — render() short-circuits when getEnabled() is false,
  // so the cached grid/shader state survives toggles. MapLibre frees it when the map is destroyed.
  useEffect(() => {
    if (!map) return;
    const tryAddFogLayer = (): void => {
      try {
        if (map.getLayer(FOG_LAYER_ID)) {
          if (fogLayerRef.current) return;
          // Orphan from a prior hook mount — remove so we can re-own it with a fresh
          // instance; otherwise resetGrid/getCircles can never reach the on-map layer
          // and lastCircleCount gets stuck past hydration.
          map.removeLayer(FOG_LAYER_ID);
        }
        const layer = new FogOfWarLayer({
          getCircles: () => selectFogCircles(store.getState()),
          getEnabled: () => enabledRef.current,
          getShouldRunWhenUnfocused: () => isMiniMapModeRef.current,
          getMapBgColor: () => mapBgColorRef.current
        });
        fogLayerRef.current = layer;
        const beforeId = getFogInsertBeforeLayerId(map);
        if (beforeId && map.getLayer(beforeId)) {
          map.addLayer(layer, beforeId);
        } else {
          map.addLayer(layer);
        }
      } catch {
        // Style not ready; a later `styledata` event will retry.
      }
    };
    const applyFogLayerState = (): void => {
      tryAddFogLayer();
      if (enabledRef.current) {
        syncFogLayerOrder(map);
      }
    };
    map.on('styledata', applyFogLayerState);
    const delayId = setTimeout(applyFogLayerState, 200);
    return () => {
      clearTimeout(delayId);
      map.off('styledata', applyFogLayerState);
    };
  }, [map, store.getState]);
  useEffect(() => {
    isMiniMapModeRef.current = isMiniMapMode;
    fogLayerRef.current?.syncAnimationState();
  }, [isMiniMapMode]);
  const prevHydrationVersionRef = useRef(hydrationVersion);
  useEffect(() => {
    if (hydrationVersion === prevHydrationVersionRef.current) return;
    prevHydrationVersionRef.current = hydrationVersion;
    fogLayerRef.current?.resetGrid();
    map?.triggerRepaint();
  }, [hydrationVersion, map]);
  return {
    isFogLoginModalOpen,
    setIsFogLoginModalOpen,
    fogReady
  };
}